import { Suspense, lazy, useEffect, useState, Fragment } from 'react';
import classNames from 'classnames';
import { TransitionGroup, Transition } from 'react-transition-group';
import DecoderText from 'components/DecoderText';
import { useInterval, usePrevious, useWindowSize } from 'hooks';
import { reflow } from 'utils/transition';
import prerender from 'utils/prerender';
import { media } from 'utils/style';
import { ReactComponent as ArrowDown } from 'assets/arrow-down.svg';
import { tokens } from 'components/ThemeProvider/theme';
import Heading from 'components/Heading';
import Section from 'components/Section';
import { useTheme } from 'components/ThemeProvider';
import VisuallyHidden from 'components/VisuallyHidden';
import './Intro.css';
import { Button } from 'components/Button';
import ReactGA from 'react-ga';
import Image from 'components/Image';
import ProfileImg from 'assets/aryash.jpg';
function Intro({ id, sectionRef, disciplines, scrollIndicatorHidden, ...rest }) {
  const theme = useTheme();
  const [disciplineIndex, setDisciplineIndex] = useState(0);
  const windowSize = useWindowSize();
  const prevTheme = usePrevious(theme);
  const introLabel = [disciplines.slice(0, -1).join(', '), disciplines.slice(-1)[0]].join(
    ', and '
  );
  const currentDisciplines = disciplines.filter(
    (item, index) => index === disciplineIndex
  );
  const titleId = `${id}-title`;

  useInterval(
    () => {
      const index = (disciplineIndex + 1) % disciplines.length;
      setDisciplineIndex(index);
    },
    5000,
    theme.themeId
  );

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    if (prevTheme && prevTheme.themeId !== theme.themeId) {
      setDisciplineIndex(0);
    }
  }, [theme.themeId, prevTheme]);

  return (
    <Section
      className="intro"
      as="section"
      ref={sectionRef}
      id={id}
      aria-labelledby={titleId}
      tabIndex={-1}
      {...rest}
    >
      <Transition
        key={theme.themeId}
        appear={!prerender}
        in={!prerender}
        timeout={3000}
        onEnter={reflow}
      >
        {status => (
          <Fragment>
            {!prerender && <Suspense fallback={null}></Suspense>}
            <div className="intro_row">
              <Image
                className="intro_image_aryash"
                reveal
                delay={100}
                borderRadius={true}
                srcSet={`${ProfileImg} 480w, ${ProfileImg} 960w`}
                sizes={`(max-width: ${media.mobile}px) 100vw, 600px`}
                alt="Aryash Dubey"
              />{' '}
              <header className="intro__text">
                <h1
                  className={classNames('intro__name', `intro__name--${status}`)}
                  id={titleId}
                >
                  <DecoderText
                    text="Aryash Dubey"
                    style={{ fontSize: '40px' }}
                    start={!prerender}
                    delay={300}
                  />
                </h1>
                <Heading level={0} as="h2" className="intro__title">
                  <TransitionGroup
                    className={classNames('intro__title-row', {
                      'intro__title-row--hidden': prerender,
                    })}
                    component="span"
                  >
                    {currentDisciplines.map(item => (
                      <Transition
                        appear
                        timeout={{ enter: 3000, exit: 2000 }}
                        key={item}
                        onEnter={reflow}
                      >
                        {wordStatus => (
                          <span
                            aria-hidden
                            className={classNames(
                              'intro__title-word',
                              'intro__title-word--plus',
                              `intro__title-word--${wordStatus}`
                            )}
                            style={{ '--delay': tokens.base.durationL }}
                          >
                            {item}
                          </span>
                        )}
                      </Transition>
                    ))}
                  </TransitionGroup>
                  <VisuallyHidden className="intro__title-label">{`Designer ${introLabel}`}</VisuallyHidden>
                </Heading>{' '}
                <div style={{ height: '32px' }} />
                <Button iconHoverShift href="https://drive.google.com/file/d/1ymvH0Wj3G7Pl-0wONgU8FQhnryIR3EX8/view?usp=share_link" iconEnd="arrowRight">
                  Download Resume
                </Button>
              </header>
            </div>

            {windowSize.width > media.tablet && (
              <div
                className={classNames(
                  'intro__scroll-indicator',
                  `intro__scroll-indicator--${status}`,
                  { 'intro__scroll-indicator--hidden': scrollIndicatorHidden }
                )}
                status={status}
              />
            )}
            {windowSize.width <= media.tablet && (
              <div
                className={classNames(
                  'intro__mobile-scroll-indicator',
                  `intro__mobile-scroll-indicator--${status}`,
                  { 'intro__mobile-scroll-indicator--hidden': scrollIndicatorHidden }
                )}
              >
                <ArrowDown aria-hidden />
              </div>
            )}
          </Fragment>
        )}
      </Transition>
    </Section>
  );
}

export default Intro;
